import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	CircularProgress,
	Divider,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import type { ItemRoll, PurchaseOrderItem } from "../models/RollsReceived";

interface RollForm {
	Id: number | null;
	Name: string;
	Hardness: number | null;
	SupplierInvoiceNumber: string;
}

interface Props {
	item: PurchaseOrderItem | null;
	roll: ItemRoll | null;
	isNewMode: boolean;
	writesLocked: boolean;
	isSaving: boolean;
	onSave: (form: RollForm) => void;
}

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

const emptyState = (text: string) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			height: "100%",
			color: "text.secondary",
		}}
	>
		<Typography>{text}</Typography>
	</Box>
);

const ReadOnlyField = ({
	label,
	value,
}: {
	label: string;
	value: string | number;
}) => (
	<TextField
		label={label}
		value={value === "" ? "-" : value}
		fullWidth
		size="small"
		slotProps={{
			inputLabel: { shrink: true },
			input: { readOnly: true },
			htmlInput: { tabIndex: -1 },
		}}
		sx={{
			"& .MuiOutlinedInput-root": {
				"& fieldset": { borderColor: "divider", borderWidth: 1 },
				"&:hover fieldset": { borderColor: "divider", borderWidth: 1 },
				"&.Mui-focused fieldset": { borderColor: "divider", borderWidth: 1 },
			},
			"& .MuiInputBase-input": { cursor: "default" },
		}}
	/>
);

export const RollDetailViewer = ({
	item,
	roll,
	isNewMode,
	writesLocked,
	isSaving,
	onSave,
}: Props) => {
	const [form, setForm] = useState<RollForm | null>(null);

	useEffect(() => {
		if (roll) {
			setForm({
				Id: roll.Id,
				Name: roll.Name ?? "",
				Hardness: roll.Hardness,
				SupplierInvoiceNumber: roll.SupplierInvoiceNumber ?? "",
			});
		} else {
			setForm(null);
		}
	}, [roll]);

	// Hardness is a non-negative integer.
	const hardnessError = useMemo(() => {
		if (form?.Hardness == null) return null;
		if (!Number.isInteger(form.Hardness) || form.Hardness < 0)
			return "Must be an integer ≥ 0";
		return null;
	}, [form?.Hardness]);

	// Out-of-range vs the roll blank's spec. Mirrors the SP guard: a 0 (or NULL)
	// bound means "no spec" — only enforce real limits (min > 0 / max > 0).
	const hardnessRangeError = useMemo(() => {
		const h = form?.Hardness;
		if (h == null) return null;
		const min = item?.HardnessMinimum ?? 0;
		const max = item?.HardnessMaximum ?? 0;
		if ((min > 0 && h < min) || (max > 0 && h > max))
			return `Must be between ${min} and ${max}`;
		return null;
	}, [form?.Hardness, item?.HardnessMinimum, item?.HardnessMaximum]);

	if (!item) return emptyState("Select a purchase order item");
	if (!roll || !form) return emptyState("Select a roll slot to edit");

	const isDirty =
		(form.Name ?? "") !== (roll.Name ?? "") ||
		form.Hardness !== roll.Hardness ||
		(form.SupplierInvoiceNumber ?? "") !== (roll.SupplierInvoiceNumber ?? "");
	const canSave =
		isNewMode &&
		isDirty &&
		form.Name.trim().length > 0 &&
		form.Hardness != null &&
		form.Hardness >= 0 &&
		!hardnessError &&
		!hardnessRangeError;

	const handleChange = (field: keyof RollForm, value: unknown) =>
		setForm((prev) => (prev ? { ...prev, [field]: value } : prev));

	const hardnessRange =
		item.HardnessMinimum != null || item.HardnessMaximum != null
			? `${item.HardnessMinimum ?? "-"} / ${item.HardnessMaximum ?? "-"}`
			: "-";

	return (
		<Box sx={{ height: "100%", overflow: "auto" }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
						{`Item ${item.ItemNumber} · Slot ${roll.Order} of ${item.QuantityOrdered ?? "-"}`}
					</Typography>
					{isSaving && <CircularProgress size={16} />}
				</Box>
				<WriteLockTooltip locked={writesLocked}>
					<Button
						color="primary"
						variant="contained"
						size="small"
						onClick={() => form && onSave(form)}
						disabled={!canSave || isSaving}
					>
						SAVE
					</Button>
				</WriteLockTooltip>
			</Box>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Order Item</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<ReadOnlyField label="Item #" value={item.ItemNumber ?? "-"} />
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<ReadOnlyField
						label="Hardness range (min/max)"
						value={hardnessRange}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<ReadOnlyField
						label="Blank Diameter"
						value={item.RollBlankDiameter ?? "-"}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12 }}>
					<Typography sx={sectionHeaderSx}>Received Roll</Typography>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Foundry Number"
						value={form.Name}
						onChange={(e) => handleChange("Name", e.target.value)}
						fullWidth
						size="small"
						required
						disabled={!isNewMode}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Hardness"
						type="number"
						value={form.Hardness ?? ""}
						onChange={(e) => {
							const raw = e.target.value;
							handleChange(
								"Hardness",
								raw === "" ? null : Math.max(0, Math.trunc(Number(raw) || 0)),
							);
						}}
						fullWidth
						size="small"
						required
						disabled={!isNewMode}
						error={!!hardnessError || !!hardnessRangeError}
						helperText={hardnessError ?? hardnessRangeError ?? undefined}
						slotProps={{
							inputLabel: { shrink: true },
							htmlInput: { min: 0, step: 1 },
						}}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Supplier Invoice No."
						value={form.SupplierInvoiceNumber}
						onChange={(e) =>
							handleChange("SupplierInvoiceNumber", e.target.value)
						}
						fullWidth
						size="small"
						disabled={!isNewMode}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
